import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * @param {Record<string, unknown>[]} rows
 * @param {{ sheetName?: string, fileName: string, header?: string[] }} options
 */
const buildWorksheet = (rows, header) => {
  if (rows?.length) {
    return header
      ? XLSX.utils.json_to_sheet(rows, { header })
      : XLSX.utils.json_to_sheet(rows);
  }
  if (header?.length) {
    return XLSX.utils.aoa_to_sheet([header]);
  }
  return XLSX.utils.aoa_to_sheet([['No records']]);
};

export const exportRowsToExcel = (rows, { sheetName = 'Sheet1', fileName, header } = {}) => {
  if (!rows?.length) throw new Error('No data to export');
  const ws = buildWorksheet(rows, header);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const name = fileName?.endsWith('.xlsx') ? fileName : `${fileName || 'export'}.xlsx`;
  XLSX.writeFile(wb, name);
};

/**
 * @param {{ sheetName: string, rows: Record<string, unknown>[], header?: string[] }[]} sheets
 * @param {{ fileName: string }} options
 */
export const exportMultiSheetExcel = (sheets, { fileName } = {}) => {
  if (!sheets?.length) throw new Error('No sheets to export');
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ sheetName = 'Sheet1', rows = [], header }) => {
    const ws = buildWorksheet(rows, header);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  });
  const name = fileName?.endsWith('.xlsx') ? fileName : `${fileName || 'export'}.xlsx`;
  XLSX.writeFile(wb, name);
};

const appendExcelSection = (rowsAoa, title, header, rows) => {
  rowsAoa.push([]);
  rowsAoa.push([title]);
  rowsAoa.push(header);
  if (rows?.length) {
    rows.forEach((row) => {
      rowsAoa.push(header.map((key) => row?.[key] ?? ''));
    });
    return;
  }
  rowsAoa.push(['No records']);
};

/**
 * Payroll Excel export with pay run, joined, and exited sections on the main sheet.
 * @param {{
 *   fileName: string,
 *   reportTitle: string,
 *   payRunHeader: string[],
 *   payRunRows: Record<string, unknown>[],
 *   joinedHeader: string[],
 *   joinedRows: Record<string, unknown>[],
 *   exitedHeader: string[],
 *   exitedRows: Record<string, unknown>[],
 *   joinedTitle?: string,
 *   exitedTitle?: string,
 * }} options
 */
export const exportPayrollExcel = ({
  fileName,
  reportTitle,
  payRunHeader,
  payRunRows,
  joinedHeader,
  joinedRows,
  exitedHeader,
  exitedRows,
  joinedTitle = 'Employees Joined',
  exitedTitle = 'Employees Exited',
}) => {
  const combined = [[reportTitle], payRunHeader];
  payRunRows.forEach((row) => {
    combined.push(payRunHeader.map((key) => row?.[key] ?? ''));
  });
  appendExcelSection(combined, joinedTitle, joinedHeader, joinedRows);
  appendExcelSection(combined, exitedTitle, exitedHeader, exitedRows);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(combined), 'PayRun');
  XLSX.utils.book_append_sheet(
    wb,
    buildWorksheet(joinedRows, joinedHeader),
    'Joined'
  );
  XLSX.utils.book_append_sheet(
    wb,
    buildWorksheet(exitedRows, exitedHeader),
    'Exited'
  );

  const name = fileName?.endsWith('.xlsx') ? fileName : `${fileName || 'export'}.xlsx`;
  XLSX.writeFile(wb, name);
};

const isPdfNumericCell = (value) => /^-?\d+(\.\d+)?$/.test(String(value ?? '').trim());

const fitPdfColumnWidths = (weights, usableWidth, minWidths) => {
  const count = weights.length;
  const locked = new Array(count).fill(false);
  const widths = new Array(count).fill(0);
  let remainingWidth = usableWidth;
  let remainingWeight = weights.reduce((sum, w) => sum + w, 0) || count;

  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < count; i += 1) {
      if (locked[i] || remainingWeight <= 0) continue;
      const nextWidth = (weights[i] / remainingWeight) * remainingWidth;
      const minWidth = minWidths[i] ?? 0;
      if (nextWidth < minWidth) {
        widths[i] = minWidth;
        remainingWidth = Math.max(remainingWidth - minWidth, 0);
        remainingWeight = Math.max(remainingWeight - weights[i], 0);
        locked[i] = true;
        changed = true;
      }
    }
  }

  for (let i = 0; i < count; i += 1) {
    if (locked[i]) continue;
    widths[i] =
      remainingWeight > 0 ? (weights[i] / remainingWeight) * remainingWidth : (minWidths[i] ?? 0);
  }

  const total = widths.reduce((sum, w) => sum + w, 0) || 1;
  return widths.map((w) => (w / total) * usableWidth);
};

/**
 * Build PDF column widths from heading length and sample cell content.
 * Long headers wrap instead of stealing space from names and amounts.
 * @param {string[]} headings
 * @param {number} usableWidth
 * @param {unknown[][]} [bodyRows]
 * @param {{ columnWeights?: Record<string, number>, numericColumns?: string[], compactColumns?: string[] }} [options]
 */
export const getPdfColumnWidthsFromHeadings = (headings, usableWidth, bodyRows = [], options = {}) => {
  const customWeights = options?.columnWeights && typeof options.columnWeights === 'object'
    ? options.columnWeights
    : {};
  const numericColumnSet = new Set(options?.numericColumns || []);
  const compactColumnSet = new Set(options?.compactColumns || []);
  const sample = bodyRows.slice(0, 40);

  const inferredNumeric = headings.map((heading, colIndex) => {
    if (numericColumnSet.has(heading)) return true;
    let sampled = 0;
    let numeric = 0;
    sample.forEach((row) => {
      const cell = row?.[colIndex];
      if (cell == null || String(cell).trim() === '') return;
      sampled += 1;
      if (isPdfNumericCell(cell)) numeric += 1;
    });
    return sampled > 0 && numeric / sampled >= 0.7;
  });

  const weights = headings.map((heading, colIndex) => {
    const custom = Number(customWeights[heading]);
    if (Number.isFinite(custom) && custom > 0) return custom;

    const headingWeight = Math.min(Math.max(String(heading || '').length, 4), 10);
    let contentWeight = 0;
    sample.forEach((row) => {
      contentWeight = Math.max(contentWeight, String(row?.[colIndex] ?? '').length);
    });

    if (inferredNumeric[colIndex]) {
      return Math.max(9, Math.min(Math.max(contentWeight + 1, headingWeight * 0.5), 12));
    }
    return Math.max(headingWeight, Math.min(contentWeight, 26), 12);
  });

  const minWidths = headings.map((heading, colIndex) => {
    const key = String(heading || '').toLowerCase();
    if (key.includes('sl') && key.includes('no')) return 8;
    if (key === 'days') return 9;
    if (compactColumnSet.has(heading)) return 10;
    if (inferredNumeric[colIndex]) return 14;
    if (key.includes('name')) return 18;
    return 12;
  });

  const widths = fitPdfColumnWidths(weights, usableWidth, minWidths);

  return headings.reduce((acc, heading, index) => {
    const key = String(heading || '').toLowerCase();
    const isSerial = key.includes('sl') && key.includes('no');
    acc[index] = {
      cellWidth: widths[index],
      ...(inferredNumeric[index] && !isSerial ? { halign: 'right' } : {}),
    };
    return acc;
  }, {});
};

/**
 * @param {Record<string, unknown>[]} rows
 * @param {{
 *   title?: string,
 *   fileName: string,
 *   header?: string[],
 *   orientation?: 'portrait' | 'landscape',
 * }} options
 */
export const exportRowsToPdf = (
  rows,
  { title = 'Export', fileName, header, orientation } = {}
) => {
  if (!rows?.length) throw new Error('No data to export');

  const headings = header?.length ? header : Object.keys(rows[0] || {});
  if (!headings.length) throw new Error('No columns to export');

  const colCount = headings.length;
  const resolvedOrientation =
    orientation || (colCount > 8 ? 'landscape' : 'portrait');

  const doc = new jsPDF({
    orientation: resolvedOrientation,
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const usableWidth = pageWidth - margin * 2;

  doc.setFontSize(14);
  doc.setTextColor(0, 71, 171);
  doc.text(String(title), margin, 14);

  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 20);

  const body = rows.map((row) =>
    headings.map((key) => {
      const value = row?.[key];
      if (value == null) return '';
      return String(value);
    })
  );

  autoTable(doc, {
    startY: 24,
    head: [headings],
    body,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: colCount > 12 ? 6 : colCount > 8 ? 7 : 8,
      cellPadding: 1.5,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [0, 71, 171],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: colCount > 12 ? 6 : 7,
    },
    columnStyles: getPdfColumnWidthsFromHeadings(headings, usableWidth, body),
    tableWidth: usableWidth,
  });

  const name = fileName?.endsWith('.pdf') ? fileName : `${fileName || 'export'}.pdf`;
  doc.save(name);
};

const cellToPdfText = (value) => {
  if (value == null || value === '') return ' ';
  return String(value);
};

const appendPdfTableSection = (doc, {
  sectionTitle,
  rows = [],
  header,
  footerRow,
  startY,
  margin,
  usableWidth,
  orientation,
  columnWeights,
  numericColumns,
  compactColumns,
  headerLabels,
  hideHead = false,
  align = 'left',
  tableWidth,
  highlightRowLabels = [],
  bottomGap,
}) => {
  const headings = header?.length ? header : Object.keys(rows[0] || {});
  let cursorY = startY;

  if (sectionTitle) {
    doc.setFontSize(11);
    doc.setTextColor(0, 71, 171);
    doc.text(String(sectionTitle), margin, cursorY);
    cursorY += 6;
  }

  if (!headings.length) {
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('No records', margin, cursorY);
    return cursorY + 8;
  }

  const body = rows.length
    ? rows.map((row) => headings.map((key) => cellToPdfText(row?.[key])))
    : [['No records']];

  // Keep totals in the body grid so empty cells still occupy their column widths.
  const totalRowIndex =
    footerRow && rows.length
      ? body.push(headings.map((key) => cellToPdfText(footerRow?.[key]))) - 1
      : -1;

  const colCount = headings.length;
  const compact = colCount > 10;
  const numericColumnSet = new Set(numericColumns || []);
  const highlightSet = new Set(highlightRowLabels || []);
  const resolvedTableWidth =
    Number.isFinite(Number(tableWidth)) && Number(tableWidth) > 0
      ? Math.min(Number(tableWidth), usableWidth)
      : usableWidth;
  const leftMargin = align === 'right' ? margin + usableWidth - resolvedTableWidth : margin;
  const columnStyles = hideHead
    ? {
        0: { cellWidth: resolvedTableWidth * 0.72 },
        1: { cellWidth: resolvedTableWidth * 0.28, halign: 'right' },
      }
    : getPdfColumnWidthsFromHeadings(
        headings,
        resolvedTableWidth,
        body.length && body[0]?.length === headings.length ? body : [],
        { columnWeights, numericColumns, compactColumns }
      );
  const headLabels = headings.map((key) => headerLabels?.[key] || key);

  autoTable(doc, {
    startY: cursorY,
    head: [headLabels],
    body,
    showHead: hideHead ? 'never' : 'everyPage',
    margin: { left: leftMargin, right: margin },
    styles: {
      fontSize: hideHead ? 8 : compact ? 6.5 : colCount > 8 ? 7 : 8,
      cellPadding: hideHead ? 1.6 : compact ? 0.9 : 1.5,
      overflow: 'linebreak',
      valign: 'middle',
      lineWidth: hideHead ? 0.25 : 0,
      lineColor: hideHead ? [15, 23, 42] : [255, 255, 255],
    },
    headStyles: {
      fillColor: [0, 71, 171],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: compact ? 5.5 : colCount > 8 ? 6 : 7,
      cellPadding: compact ? 1 : 1.5,
      overflow: 'linebreak',
      halign: 'center',
      valign: 'middle',
    },
    columnStyles,
    tableWidth: resolvedTableWidth,
    didParseCell: (data) => {
      const heading = headings[data.column.index];
      const isNumeric = numericColumnSet.has(heading) || columnStyles?.[data.column.index]?.halign === 'right';
      if (data.section !== 'head' && isNumeric) {
        data.cell.styles.halign = 'right';
        data.cell.styles.overflow = 'hidden';
      }
      if (data.section === 'body' && data.row.index === totalRowIndex) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
        data.cell.styles.textColor = [15, 23, 42];
        data.cell.styles.overflow = 'hidden';
      }
      const rowLabel = rows?.[data.row.index]?.[headings[0]];
      if (data.section === 'body' && highlightSet.has(String(rowLabel ?? ''))) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [241, 245, 249];
      }
    },
  });

  return (doc.lastAutoTable?.finalY ?? cursorY) + (bottomGap ?? (orientation === 'landscape' ? 10 : 12));
};

const appendPdfSplitRow = (doc, section, { startY, margin, usableWidth, orientation }) => {
  const gap = 8;
  const rightWidth = Math.min(Number(section?.right?.tableWidth) || 90, usableWidth * 0.38);
  const leftWidth = Math.max(usableWidth - gap - rightWidth, 40);
  const leftSections = Array.isArray(section?.left) ? section.left : [];

  let leftY = startY;
  leftSections.forEach((item, index) => {
    leftY = appendPdfTableSection(doc, {
      sectionTitle: item?.title,
      rows: item?.rows || [],
      header: item?.header,
      footerRow: item?.footerRow,
      startY: leftY,
      margin,
      usableWidth: leftWidth,
      orientation,
      columnWeights: item?.columnWeights,
      numericColumns: item?.numericColumns,
      compactColumns: item?.compactColumns,
      headerLabels: item?.headerLabels,
      hideHead: item?.hideHead,
      align: 'left',
      tableWidth: leftWidth,
      highlightRowLabels: item?.highlightRowLabels,
      bottomGap: index < leftSections.length - 1 ? 6 : 0,
    });
  });

  const right = section?.right;
  const rightY = right
    ? appendPdfTableSection(doc, {
        sectionTitle: right?.title,
        rows: right?.rows || [],
        header: right?.header,
        footerRow: right?.footerRow,
        startY,
        margin,
        usableWidth,
        orientation,
        columnWeights: right?.columnWeights,
        numericColumns: right?.numericColumns,
        compactColumns: right?.compactColumns,
        headerLabels: right?.headerLabels,
        hideHead: right?.hideHead,
        align: 'right',
        tableWidth: rightWidth,
        highlightRowLabels: right?.highlightRowLabels,
        bottomGap: 0,
      })
    : startY;

  const trailing = orientation === 'landscape' ? 10 : 12;
  return Math.max(leftY, rightY) + trailing;
};

/**
 * @param {{ title?: string, rows: Record<string, unknown>[], header?: string[], footerRow?: Record<string, unknown> }[]} sections
 * @param {{ title?: string, fileName: string, orientation?: 'portrait' | 'landscape' }} options
 */
export const exportMultiSectionPdf = (
  sections,
  { title = 'Export', fileName, orientation = 'portrait' } = {}
) => {
  if (!sections?.length) throw new Error('No sections to export');

  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const usableWidth = pageWidth - margin * 2;

  doc.setFontSize(14);
  doc.setTextColor(0, 71, 171);
  doc.text(String(title), margin, 14);

  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 20);

  let cursorY = 26;

  sections.forEach((section, index) => {
    if (cursorY > pageHeight - 40) {
      doc.addPage();
      cursorY = margin + 6;
    }

    if (section?.layout === 'split') {
      cursorY = appendPdfSplitRow(doc, section, {
        startY: cursorY,
        margin,
        usableWidth,
        orientation,
      });
      if (index < sections.length - 1) {
        cursorY += 4;
      }
      return;
    }

    cursorY = appendPdfTableSection(doc, {
      sectionTitle: section?.title,
      rows: section?.rows || [],
      header: section?.header,
      footerRow: section?.footerRow,
      startY: cursorY,
      margin,
      usableWidth,
      orientation,
      columnWeights: section?.columnWeights,
      numericColumns: section?.numericColumns,
      compactColumns: section?.compactColumns,
      headerLabels: section?.headerLabels,
      hideHead: section?.hideHead,
      align: section?.align,
      tableWidth: section?.tableWidth,
      highlightRowLabels: section?.highlightRowLabels,
    });

    if (index < sections.length - 1) {
      cursorY += 4;
    }
  });

  const name = fileName?.endsWith('.pdf') ? fileName : `${fileName || 'export'}.pdf`;
  doc.save(name);
};

/**
 * Export rows as Excel or PDF.
 * @param {'excel' | 'pdf'} format
 * @param {Record<string, unknown>[]} rows
 * @param {{
 *   title?: string,
 *   fileName: string,
 *   sheetName?: string,
 *   header?: string[],
 *   orientation?: 'portrait' | 'landscape',
 * }} options
 */
export const exportRowsAs = async (format, rows, options = {}) => {
  if (format === 'pdf') {
    exportRowsToPdf(rows, options);
    return;
  }
  exportRowsToExcel(rows, options);
};

/**
 * Rename header cells in an Excel Blob (first row of each sheet).
 * Matching is case-insensitive and trims whitespace.
 * @param {Blob} blob
 * @param {Record<string, string>} renames e.g. { 'Reviewed By': 'Approved By' }
 * @returns {Promise<Blob>}
 */
export const renameExcelHeaderColumns = async (blob, renames = {}) => {
  if (!blob || !Object.keys(renames).length) return blob;

  const lookup = Object.fromEntries(
    Object.entries(renames).map(([from, to]) => [String(from).trim().toLowerCase(), to])
  );

  const buffer = await blob.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet?.['!ref']) return;
    const range = XLSX.utils.decode_range(sheet['!ref']);
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: col });
      const cell = sheet[cellAddress];
      if (!cell) continue;
      const current = String(cell.v ?? '').trim();
      const next = lookup[current.toLowerCase()];
      if (!next) continue;
      cell.v = next;
      cell.w = next;
      cell.t = 's';
    }
  });

  const out = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], {
    type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Trigger download of an existing Blob (e.g. server Excel export).
 * @param {Blob} blob
 * @param {string} fileName
 */
export const downloadBlobFile = (blob, fileName) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};
